import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Search } from 'lucide-react';
import type { AccountType, CustomerSlaRow } from '@metron/shared';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { DataTable } from '../components/data-table/data-table';
import { DataTableColumnHeader } from '../components/data-table/data-table-column-header';
import { useTableUrlState } from '../hooks/useTableUrlState.ts';
import { useReadRateSlaByCustomer } from '../hooks/useReadRateSlaByCustomer.ts';
import { sla1dColorClass, sla3dColorClass } from '../lib/utils';
import { InstalledMetersTooltip } from '../components/InstalledMetersTooltip.tsx';
import { toErrorMessage } from '@metron/shared';

function formatSla(value: number | null): string {
  if (value === null) return '—';
  return `${value.toFixed(2)}%`;
}

const columns: ColumnDef<CustomerSlaRow>[] = [
  {
    accessorKey: 'customerName',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Customer' />,
    cell: ({ row }) => (
      <Link to={`/customers/${row.original.customerId}`} className='font-medium hover:underline'>
        {row.getValue('customerName')}
      </Link>
    ),
  },
  {
    accessorKey: 'accountType',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Type' />,
    cell: ({ row }) => row.original.accountType ?? '—',
  },
  {
    accessorKey: 'installedSensors30d',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={<InstalledMetersTooltip>Installed</InstalledMetersTooltip>}
      />
    ),
    cell: ({ row }) => (
      <div className='text-right'>
        {(row.getValue('installedSensors30d') as number).toLocaleString()}
      </div>
    ),
  },
  {
    accessorKey: 'totalSensors30d',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Total' />,
    cell: ({ row }) => (
      <div className='text-right'>
        {(row.getValue('totalSensors30d') as number).toLocaleString()}
      </div>
    ),
  },
  {
    id: 'reported1d',
    accessorKey: 'sensorsReported1d',
    header: ({ column }) => <DataTableColumnHeader column={column} title='1-Day Reported' />,
    cell: ({ row }) => (
      <div className='text-right'>
        {row.original.sensorsReported1d.toLocaleString()} /{' '}
        {row.original.installedSensors30d.toLocaleString()}
      </div>
    ),
  },
  {
    accessorKey: 'sla1d',
    header: ({ column }) => <DataTableColumnHeader column={column} title='1-Day SLA' />,
    cell: ({ row }) => {
      const value = row.getValue('sla1d') as number | null;
      return (
        <div className={`text-right font-medium ${sla1dColorClass(value)}`}>
          {formatSla(value)}
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.sla1d;
      const b = rowB.original.sla1d;
      if (a === null && b === null) return 0;
      if (a === null) return -1;
      if (b === null) return 1;
      return a - b;
    },
  },
  {
    id: 'reported3d',
    accessorKey: 'sensorsReported3d',
    header: ({ column }) => <DataTableColumnHeader column={column} title='3-Day Reported' />,
    cell: ({ row }) => (
      <div className='text-right'>
        {row.original.sensorsReported3d.toLocaleString()} /{' '}
        {row.original.installedSensors30d.toLocaleString()}
      </div>
    ),
  },
  {
    accessorKey: 'sla3d',
    header: ({ column }) => <DataTableColumnHeader column={column} title='3-Day SLA' />,
    cell: ({ row }) => {
      const value = row.getValue('sla3d') as number | null;
      return (
        <div className={`text-right font-medium ${sla3dColorClass(value)}`}>
          {formatSla(value)}
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.sla3d;
      const b = rowB.original.sla3d;
      if (a === null && b === null) return 0;
      if (a === null) return -1;
      if (b === null) return 1;
      return a - b;
    },
  },
];

export function ReadRateSlaByCustomerPage() {
  const { data: response, isLoading, error } = useReadRateSlaByCustomer();
  const [searchQuery, setSearchQuery] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState<AccountType | 'all'>('all');
  const tableState = useTableUrlState({
    defaultSorting: [{ id: 'sla1d', desc: false }],
  });

  const data = response?.data ?? [];
  const asOfDate = response?.asOfDate ?? '';

  const filteredData = useMemo(() => {
    let filtered = data;
    if (accountTypeFilter !== 'all') {
      filtered = filtered.filter((row) => row.accountType === accountTypeFilter);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((row) => row.customerName.toLowerCase().includes(query));
    }
    return filtered;
  }, [data, searchQuery, accountTypeFilter]);

  if (isLoading) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <h1 className='text-3xl font-bold mb-6'>Read-rate SLA by Customer</h1>
        <div className='flex items-center justify-center h-64'>
          <p className='text-muted-foreground'>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <h1 className='text-3xl font-bold mb-6'>Read-rate SLA by Customer</h1>
        <Card>
          <CardContent className='py-8'>
            <p className='text-red-500'>
              Error: {toErrorMessage(error)}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='container mx-auto px-4 py-8 space-y-6'>
      <div>
        <div className='flex items-center justify-between'>
          <h1 className='text-3xl font-bold'>Read-rate SLA by Customer</h1>
          <p className='text-muted-foreground'>As of {asOfDate}</p>
        </div>
        <p className='text-sm text-muted-foreground mt-1'>
          SLA percentages are calculated from the previous day's sensor reporting data.
        </p>
      </div>

      <div className='flex items-center gap-2'>
        <div className='relative flex-1'>
          <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
          <Input
            placeholder='Search customers by name...'
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            className='pl-8'
          />
        </div>
        <Select
          value={accountTypeFilter}
          onValueChange={(value) => setAccountTypeFilter(value as AccountType | 'all')}
        >
          <SelectTrigger className='w-[160px]'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Types</SelectItem>
            <SelectItem value='Utility'>Utility</SelectItem>
            <SelectItem value='Real Estate'>Real Estate</SelectItem>
          </SelectContent>
        </Select>
        {(searchQuery || accountTypeFilter !== 'all') && (
          <Button
            variant='ghost'
            onClick={() => {
              setSearchQuery('');
              setAccountTypeFilter('all');
            }}
            size='sm'
          >
            Clear
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        data={filteredData}
        sorting={tableState.sorting}
        onSortingChange={tableState.onSortingChange}
        pagination={tableState.pagination}
        onPaginationChange={tableState.onPaginationChange}
      />
    </div>
  );
}
